import type { JSX } from 'react';
import { useId } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Input } from '@artosera/ui';
import {
  OPERATEURS_FILTRE,
  type BlocNature,
  type CiblePivot,
  type ErreurRegle,
  type FiltreRegle,
  type JointureRegle,
  type OperateurFiltre,
  type Regles,
} from '../../../../lib/correspondance.js';
import { filtreVide, refVide } from '../../../../lib/reglesEdition.js';
import reprise from '../Reprise.module.css';
import { RefEditeur } from './RefEditeur.js';
import styles from './Correspondance.module.css';

/** Ce que l'éditeur sait du profil des sources, pour proposer des colonnes. */
export interface AideSources {
  /** Les colonnes de la table qui joue ce rôle (`null` : la source principale). */
  colonnes: (role: string | null) => string[];
  /** Les feuilles ou tableaux du fichier qui joue ce rôle. */
  tables: (role: string) => string[];
  /** Les tableaux imbriqués d'un fichier JSON ou XML. */
  tableaux: string[];
}

function FiltresEditeur({
  filtres,
  onChange,
  roles,
  aide,
  avecParent,
  titre,
  vide,
}: {
  filtres: FiltreRegle[];
  onChange: (suite: FiltreRegle[]) => void;
  roles: string[];
  aide: AideSources;
  avecParent: boolean;
  titre: string;
  vide: string;
}): JSX.Element {
  const { t } = useTranslation('console');
  const id = useId();
  const majFiltre = (rang: number, suite: Partial<FiltreRegle>): void =>
    onChange(filtres.map((filtre, i) => (i === rang ? { ...filtre, ...suite } : filtre)));
  return (
    <div className={reprise.pile}>
      <h4 className={reprise.sousTitre}>{titre}</h4>
      {filtres.length === 0 ? <p className={reprise.discret}>{vide}</p> : null}
      {filtres.map((filtre, rang) => (
        <div key={rang} className={styles.ligneRegle}>
          <RefEditeur
            valeur={filtre.source}
            libelle={t('correspondance.generales.filtreColonne')}
            suggestions={aide.colonnes(filtre.source.role)}
            roles={roles}
            avecParent={avecParent}
            onChange={(source) => majFiltre(rang, { source })}
          />
          <div className={styles.parametre}>
            <label htmlFor={`${id}-op-${String(rang)}`}>
              {t('correspondance.generales.filtreOperateur')}
            </label>
            <select
              id={`${id}-op-${String(rang)}`}
              className={styles.selection}
              value={filtre.operateur}
              onChange={(e) => majFiltre(rang, { operateur: e.target.value as OperateurFiltre })}
            >
              {OPERATEURS_FILTRE.map((operateur) => (
                <option key={operateur} value={operateur}>
                  {t(`correspondance.generales.operateurs.${operateur}`)}
                </option>
              ))}
            </select>
          </div>
          {filtre.operateur === 'egal' || filtre.operateur === 'different' ? (
            <div className={styles.parametre}>
              <label htmlFor={`${id}-val-${String(rang)}`}>
                {t('correspondance.generales.filtreValeur')}
              </label>
              <Input
                id={`${id}-val-${String(rang)}`}
                value={filtre.valeur}
                maxLength={500}
                onChange={(e) => majFiltre(rang, { valeur: e.target.value })}
              />
            </div>
          ) : null}
          <Button
            variant="ghost"
            size="sm"
            aria-label={t('correspondance.generales.retirerFiltre', { rang: rang + 1 })}
            onClick={() => onChange(filtres.filter((_, i) => i !== rang))}
          >
            {t('correspondance.editeur.retirer')}
          </Button>
        </div>
      ))}
      <div className={reprise.actions}>
        <Button variant="secondary" size="sm" onClick={() => onChange([...filtres, filtreVide()])}>
          {t('correspondance.generales.ajouterFiltre')}
        </Button>
      </div>
    </div>
  );
}

/**
 * Tout ce qui n'est pas un champ : les fichiers que les règles lisent (un rôle
 * chacun), leurs jointures, le dépliage d'un tableau imbriqué, les filtres de
 * lignes, puis, pour la nature affichée, la clé d'origine, ses filtres et son
 * dédoublonnage (la scission vers d'autres natures).
 */
export function ReglesGenerales({
  regles,
  onRegles,
  nature,
  bloc,
  onBloc,
  cible,
  aide,
  erreurs,
}: {
  regles: Regles;
  onRegles: (suite: Regles) => void;
  nature: string;
  bloc: BlocNature;
  onBloc: (suite: BlocNature) => void;
  cible: CiblePivot | undefined;
  aide: AideSources;
  erreurs: readonly ErreurRegle[];
}): JSX.Element {
  const { t } = useTranslation('console');
  const id = useId();
  const roles = regles.sources.map((source) => source.role);
  const secondaires = regles.sources.slice(1);
  const avecParent = regles.deplier !== null;
  const porteOrigine = cible?.colonnes.some((colonne) => colonne.cle === 'refOrigine') ?? false;
  const champsAlimentes = Object.keys(bloc.colonnes);
  const messages = (prefixe: string): string[] =>
    erreurs
      .filter((e) => e.chemin === prefixe || e.chemin.startsWith(`${prefixe}.`))
      .map((e) => e.message);

  const majJointure = (rang: number, suite: Partial<JointureRegle>): void =>
    onRegles({
      ...regles,
      jointures: regles.jointures.map((j, i) => (i === rang ? { ...j, ...suite } : j)),
    });

  const alertes = (prefixe: string): JSX.Element | null => {
    const liste = messages(prefixe);
    return liste.length === 0 ? null : (
      <ul className={styles.erreursChamp} role="alert">
        {liste.map((message) => (
          <li key={message}>{message}</li>
        ))}
      </ul>
    );
  };

  return (
    <details
      className={styles.generales}
      open={erreurs.some((e) =>
        /^(sources|jointures|deplier|filtres)|\.(cleOrigine|dedoublonner|filtres)/.test(e.chemin),
      )}
    >
      <summary>{t('correspondance.generales.titre')}</summary>
      <div className={reprise.pile}>
        <div className={reprise.pile}>
          <h4 className={reprise.sousTitre}>{t('correspondance.generales.sources')}</h4>
          <p className={reprise.discret}>{t('correspondance.generales.sourcesAide')}</p>
          {regles.sources.map((source, rang) => (
            <div key={rang} className={styles.ligneRegle}>
              <div className={styles.parametre}>
                <label htmlFor={`${id}-role-${String(rang)}`}>
                  {t('correspondance.generales.role')}
                </label>
                <Input
                  id={`${id}-role-${String(rang)}`}
                  value={source.role}
                  maxLength={40}
                  readOnly={rang === 0}
                  onChange={(e) =>
                    onRegles({
                      ...regles,
                      sources: regles.sources.map((s, i) =>
                        i === rang ? { ...s, role: e.target.value.toLowerCase() } : s,
                      ),
                    })
                  }
                />
              </div>
              <div className={styles.parametre}>
                <label htmlFor={`${id}-feuille-${String(rang)}`}>
                  {t('correspondance.generales.feuille')}
                </label>
                <Input
                  id={`${id}-feuille-${String(rang)}`}
                  list={`${id}-tables-${String(rang)}`}
                  value={source.feuille ?? source.racine ?? ''}
                  autoComplete="off"
                  onChange={(e) =>
                    onRegles({
                      ...regles,
                      sources: regles.sources.map((s, i) =>
                        i === rang
                          ? { ...s, feuille: e.target.value === '' ? null : e.target.value }
                          : s,
                      ),
                    })
                  }
                />
                <datalist id={`${id}-tables-${String(rang)}`}>
                  {aide.tables(source.role).map((nom) => (
                    <option key={nom} value={nom} />
                  ))}
                </datalist>
                <span className={styles.aideChamp}>
                  {t('correspondance.generales.feuilleAide')}
                </span>
              </div>
              {rang > 0 ? (
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('correspondance.generales.retirerSource', { role: source.role })}
                  onClick={() =>
                    onRegles({
                      ...regles,
                      sources: regles.sources.filter((_, i) => i !== rang),
                      jointures: regles.jointures.filter((j) => j.role !== source.role),
                    })
                  }
                >
                  {t('correspondance.editeur.retirer')}
                </Button>
              ) : null}
            </div>
          ))}
          {alertes('sources')}
          <div className={reprise.actions}>
            <Button
              variant="secondary"
              size="sm"
              disabled={regles.sources.length >= 10}
              onClick={() =>
                onRegles({
                  ...regles,
                  sources: [
                    ...regles.sources,
                    {
                      role: `source${String(regles.sources.length + 1)}`,
                      feuille: null,
                      racine: null,
                    },
                  ],
                })
              }
            >
              {t('correspondance.generales.ajouterSource')}
            </Button>
          </div>
        </div>

        {secondaires.length > 0 ? (
          <div className={reprise.pile}>
            <h4 className={reprise.sousTitre}>{t('correspondance.generales.jointures')}</h4>
            <p className={reprise.discret}>{t('correspondance.generales.jointuresAide')}</p>
            {regles.jointures.map((jointure, rang) => (
              <div key={rang} className={styles.ligneRegle}>
                <div className={styles.parametre}>
                  <label htmlFor={`${id}-jr-${String(rang)}`}>
                    {t('correspondance.generales.jointureRole')}
                  </label>
                  <select
                    id={`${id}-jr-${String(rang)}`}
                    className={styles.selection}
                    value={jointure.role}
                    onChange={(e) => majJointure(rang, { role: e.target.value })}
                  >
                    {secondaires.map((source) => (
                      <option key={source.role} value={source.role}>
                        {source.role}
                      </option>
                    ))}
                  </select>
                </div>
                <RefEditeur
                  valeur={refVide(jointure.cle, jointure.role)}
                  libelle={t('correspondance.generales.jointureCle')}
                  suggestions={aide.colonnes(jointure.role)}
                  roles={[roles[0] ?? 'principal']}
                  avecParent={false}
                  onChange={(ref) => majJointure(rang, { cle: ref.champ })}
                />
                <RefEditeur
                  valeur={refVide(jointure.sur)}
                  libelle={t('correspondance.generales.jointureSur')}
                  suggestions={aide.colonnes(null)}
                  roles={[roles[0] ?? 'principal']}
                  avecParent={false}
                  onChange={(ref) => majJointure(rang, { sur: ref.champ })}
                />
                <div className={styles.parametre}>
                  <label htmlFor={`${id}-jm-${String(rang)}`}>
                    {t('correspondance.generales.jointureMode')}
                  </label>
                  <select
                    id={`${id}-jm-${String(rang)}`}
                    className={styles.selection}
                    value={jointure.mode}
                    onChange={(e) =>
                      majJointure(rang, {
                        mode: e.target.value === 'un_a_plusieurs' ? 'un_a_plusieurs' : 'un_a_un',
                      })
                    }
                  >
                    <option value="un_a_un">{t('correspondance.generales.modes.un_a_un')}</option>
                    <option value="un_a_plusieurs">
                      {t('correspondance.generales.modes.un_a_plusieurs')}
                    </option>
                  </select>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  aria-label={t('correspondance.generales.retirerJointure', { rang: rang + 1 })}
                  onClick={() =>
                    onRegles({
                      ...regles,
                      jointures: regles.jointures.filter((_, i) => i !== rang),
                    })
                  }
                >
                  {t('correspondance.editeur.retirer')}
                </Button>
              </div>
            ))}
            {alertes('jointures')}
            <div className={reprise.actions}>
              <Button
                variant="secondary"
                size="sm"
                disabled={regles.jointures.length >= secondaires.length}
                onClick={() => {
                  const libre = secondaires.find(
                    (source) => !regles.jointures.some((j) => j.role === source.role),
                  );
                  if (!libre) return;
                  onRegles({
                    ...regles,
                    jointures: [
                      ...regles.jointures,
                      { role: libre.role, cle: '', sur: '', mode: 'un_a_un' },
                    ],
                  });
                }}
              >
                {t('correspondance.generales.ajouterJointure')}
              </Button>
            </div>
          </div>
        ) : null}

        <div className={reprise.pile}>
          <h4 className={reprise.sousTitre}>{t('correspondance.generales.deplier')}</h4>
          <div className={styles.parametre}>
            <label htmlFor={`${id}-deplier`}>{t('correspondance.generales.deplierChemin')}</label>
            <Input
              id={`${id}-deplier`}
              list={`${id}-tableaux`}
              value={regles.deplier?.chemin ?? ''}
              autoComplete="off"
              onChange={(e) =>
                onRegles({
                  ...regles,
                  deplier: e.target.value.trim() === '' ? null : { chemin: e.target.value },
                })
              }
            />
            <datalist id={`${id}-tableaux`}>
              {aide.tableaux.map((chemin) => (
                <option key={chemin} value={chemin} />
              ))}
            </datalist>
            <span className={styles.aideChamp}>{t('correspondance.generales.deplierAide')}</span>
          </div>
          {alertes('deplier')}
        </div>

        <FiltresEditeur
          filtres={regles.filtres}
          onChange={(filtres) => onRegles({ ...regles, filtres })}
          roles={roles}
          aide={aide}
          avecParent={avecParent}
          titre={t('correspondance.generales.filtres')}
          vide={t('correspondance.generales.aucunFiltre')}
        />
        {alertes('filtres')}

        <div className={styles.blocNature}>
          <h4 className={reprise.sousTitre}>
            {t('correspondance.generales.natureTitre', { nature: t(`reprise.natures.${nature}`) })}
          </h4>
          {porteOrigine ? (
            <>
              <RefEditeur
                valeur={bloc.cleOrigine ?? refVide()}
                libelle={t('correspondance.generales.cleOrigine')}
                suggestions={aide.colonnes(null)}
                roles={roles}
                avecParent={avecParent}
                onChange={(cleOrigine) => onBloc({ ...bloc, cleOrigine })}
              />
              <span className={styles.aideChamp}>
                {t('correspondance.generales.cleOrigineAide')}
              </span>
              {alertes(`natures.${nature}.cleOrigine`)}
            </>
          ) : null}
          <div className={styles.parametre}>
            <label htmlFor={`${id}-dedoublonner`}>
              {t('correspondance.generales.dedoublonner')}
            </label>
            <select
              id={`${id}-dedoublonner`}
              className={styles.selection}
              value={bloc.dedoublonner ?? ''}
              onChange={(e) =>
                onBloc({ ...bloc, dedoublonner: e.target.value === '' ? null : e.target.value })
              }
            >
              <option value="">{t('correspondance.generales.dedoublonnerAucun')}</option>
              {champsAlimentes.map((cle) => (
                <option key={cle} value={cle}>
                  {cible?.colonnes.find((colonne) => colonne.cle === cle)?.entete ?? cle}
                </option>
              ))}
            </select>
            <span className={styles.aideChamp}>
              {t('correspondance.generales.dedoublonnerAide')}
            </span>
            {alertes(`natures.${nature}.dedoublonner`)}
          </div>
          <FiltresEditeur
            filtres={bloc.filtres}
            onChange={(filtres) => onBloc({ ...bloc, filtres })}
            roles={roles}
            aide={aide}
            avecParent={avecParent}
            titre={t('correspondance.generales.filtresNature')}
            vide={t('correspondance.generales.aucunFiltre')}
          />
          {alertes(`natures.${nature}.filtres`)}
        </div>
      </div>
    </details>
  );
}
